import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

export default function EventSlideshowPage() {
  const { eventCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/event/${eventCode}/slideshow`);
      setData(res.data);
      setError(null);
    } catch (err) {
      if (!data) setError('Event not found');
    } finally {
      setLoading(false);
    }
  }, [eventCode]);

  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(fetchData, 10000);
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  // Build combined photo list: featured first, then guest photos
  const allPhotos = [];
  if (data) {
    (data.featured_photos || []).forEach(p => allPhotos.push({ ...p, isFeatured: true }));
    (data.guest_photos || []).forEach(p => allPhotos.push({ ...p, isFeatured: false }));
  }

  const fadeSecs = data?.slideshow_fade_seconds || 6;

  // Auto-advance slideshow
  useEffect(() => {
    if (allPhotos.length <= 1) return;
    timerRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % allPhotos.length);
        setFading(false);
      }, 1000); // 1s crossfade
    }, fadeSecs * 1000);
    return () => clearInterval(timerRef.current);
  }, [allPhotos.length, fadeSecs]);

  // Reset index if photos list shrinks
  useEffect(() => {
    if (currentIndex >= allPhotos.length && allPhotos.length > 0) {
      setCurrentIndex(0);
    }
  }, [allPhotos.length, currentIndex]);

  // Theme
  const bgType = data?.background_type || 'gradient';
  const bgVal = data?.background_value || '#0a0a0a,#1a1a2e';
  const pc = data?.primary_color || '#e91e63';
  const bgStyle = bgType === 'gradient'
    ? { background: `linear-gradient(135deg, ${bgVal.split(',')[0]}, ${bgVal.split(',')[1] || bgVal.split(',')[0]})` }
    : { background: bgVal || '#0a0a0a' };

  if (loading) return (
    <div className="h-screen w-screen flex items-center justify-center bg-black">
      <Loader2 className="w-12 h-12 animate-spin text-white/20" />
    </div>
  );

  if (error) return (
    <div className="h-screen w-screen flex items-center justify-center bg-black">
      <p className="text-white/30 text-xl">{error}</p>
    </div>
  );

  // Waiting screen when no photos yet
  if (allPhotos.length === 0) return (
    <div className="h-screen w-screen flex flex-col items-center justify-center overflow-hidden" style={bgStyle} data-testid="slideshow-waiting">
      <div className="text-center space-y-6 animate-pulse">
        {data.couple_photo_url && (
          <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 shadow-2xl" style={{ borderColor: pc }}>
            <img
              src={data.couple_photo_url.startsWith('/') ? `${API}${data.couple_photo_url}` : data.couple_photo_url}
              alt="" className="w-full h-full object-cover"
            />
          </div>
        )}
        <h1 className="text-5xl font-bold text-white tracking-tight">{data.event_title}</h1>
        <p className="text-white/40 text-xl">Photos will appear here as guests upload them</p>
      </div>
    </div>
  );

  const currentPhoto = allPhotos[currentIndex] || allPhotos[0];
  const photoUrl = currentPhoto?.photo_url
    ? (currentPhoto.photo_url.startsWith('/') ? `${API}${currentPhoto.photo_url}` : currentPhoto.photo_url)
    : '';

  return (
    <div className="h-screen w-screen overflow-hidden relative cursor-none" style={bgStyle} data-testid="slideshow-active">
      {/* Photo */}
      <div
        className="absolute inset-0 flex items-center justify-center transition-opacity duration-1000"
        style={{ opacity: fading ? 0 : 1 }}
      >
        {photoUrl && (
          <img
            src={photoUrl}
            alt=""
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-2xl shadow-2xl"
            style={{ boxShadow: `0 0 80px ${pc}20` }}
          />
        )}
      </div>

      {/* Bottom info bar */}
      <div
        className="absolute bottom-0 left-0 right-0 transition-opacity duration-1000"
        style={{ opacity: fading ? 0 : 1 }}
      >
        <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-20 pb-8 px-10">
          <div className="max-w-4xl mx-auto flex items-end justify-between">
            <div>
              {currentPhoto.guest_name && (
                <p className="text-white/70 text-lg">
                  Photo by <span className="text-white font-medium">{currentPhoto.guest_name}</span>
                </p>
              )}
              {currentPhoto.caption && (
                <p className="text-white/50 text-base mt-1">{currentPhoto.caption}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-white/30 text-sm">{currentIndex + 1} / {allPhotos.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Event title watermark */}
      <div className="absolute top-6 left-8 opacity-30">
        <p className="text-white text-sm font-medium tracking-wider">{data.event_title}</p>
      </div>
    </div>
  );
}
